import React from 'react';
import { useAppDispatch, useAppSelector } from '@utils/hooks';
import { openFAQ, selectChangeFAQModalState } from '@redux/modal';
import usePortal from 'react-useportal';
import { rootElement } from '@utils/consts';
import Modal from '@components/Modal';
import {
  StyledFAQContainer,
  StyledFAQContent,
  StyledFAQList,
  StyledFAQSubTitle,
  StyledFAQTitle, StyledListTitle,
} from '@components/FAQ/styled';

const FAQ = (): JSX.Element => {
  const dispatch = useAppDispatch();
  const FAQModalState = useAppSelector(selectChangeFAQModalState);

  const closeFAQModal = () => {
    dispatch(openFAQ(false));
    const isFirefox = /Firefox/i.test(navigator.userAgent);
    isFirefox ? document.body.style.overflowY = 'auto' : document.body.style.overflowY = 'overlay';
  };

  const { Portal } = usePortal({
    bindTo: rootElement,
  });

  return (
    <>
      {FAQModalState && (
        <Portal>
          <Modal closePortal={closeFAQModal}>
            <StyledFAQContainer>
              <StyledFAQTitle>
                FAQ
              </StyledFAQTitle>
              <StyledFAQSubTitle>
                What is the Sahara protocol?
              </StyledFAQSubTitle>
              <StyledFAQContent>
                Sahara is a privacy protocol that empowers blockchain users with trading freedom and data protection across multiple chains.
                The DeFi ecosystem enables users to convert $SHRA tokens into synthetic assets as well as make use of the inbuilt staking
                mechanism, all on-platform and in complete privacy.
              </StyledFAQContent>
              <StyledFAQSubTitle>
                How is Sahara different from other privacy protocols
              </StyledFAQSubTitle>
              <StyledFAQList>
                <li><span>Sahara has Web3 ready for all DeFi applications</span></li>
                <li><span>You can launch on multiple networks</span></li>
                <li><span>You can swap a variety of synthetic assets on platform</span></li>
                <li><span>You can enjoy a variety of De-Fi products on the platform</span></li>
                <li><span>No slippage for transactions</span></li>
                <li><span>Browser Extension Wallet for your convenience</span></li>
                <li><span>Advanced user interface and user experience</span></li>
              </StyledFAQList>
              <StyledFAQSubTitle>
                Which blockchain is Sahara built on?
              </StyledFAQSubTitle>
              <StyledFAQContent>
                The main functionality of Sahara platform is based on Polygon network, while Sahara token is multichain - Ethereum and Polygon.
                Sahara is also building on Avalanche, Solana, and Binance Smart Chain.
              </StyledFAQContent>
              <StyledFAQSubTitle>
                What functions can I perform on the Sahara platform?
              </StyledFAQSubTitle>
              <StyledListTitle>
                On the platform you can:
              </StyledListTitle>
              <StyledFAQList>
                <li><span>deposit and withdraw cryptocurrencies and synthetic assets</span></li>
                <li><span>do private swaps with synthetic assets such as pUSD, pBTC, pGold, pTesla</span></li>
                <li><span>do farming</span></li>
                <li><span>vote for new features on the platform</span></li>
                <li><span>get staking rewards via the token model</span></li>
              </StyledFAQList>
              <StyledFAQSubTitle>
                How does Sahara ensure private transactions?
              </StyledFAQSubTitle>
              <StyledFAQContent>
                After depositing SHRA tokens, a secret key will be generated, which serves as the proof for token withdrawal.
                This means the platform’s smart contracts store the deposit data tied to the key - not the wallet.
                The deposited tokens can be withdrawn to any wallet.
                With many subsequent transactions taking place in the pool, it becomes impossible to link deposits and withdrawals
                to each other by way of this functionality - which is how Sahara ensures user privacy on the public blockchain.
              </StyledFAQContent>
              <StyledFAQSubTitle>
                What is a synthetic asset and how can I use it?
              </StyledFAQSubTitle>
              <StyledFAQContent>
                Cryptocurrency-based synthetic assets aim to give users access to a variety of different assets
                without needing to hold the underlying asset. This could be anything from fiat currencies, such as the
                United States dollar or the Japanese yen, to commodities like gold and silver - as well as index funds or
                other digital assets.
                On the Sahara platform, you can safely convert one private synthetic asset into another synthetic asset, while maintaining your privacy.
              </StyledFAQContent>
              <StyledFAQSubTitle>
                Can I exchange one private token to another privately on the platform?
              </StyledFAQSubTitle>
              <StyledFAQContent>
                Yes, this swapping functionality is in the development stage and will be added to the platform in the future.
              </StyledFAQContent>
              <StyledFAQSubTitle>
                What is the maximum slippage on the platform?
              </StyledFAQSubTitle>
              <StyledFAQContent>
                There’s zero slippage on the platform.
              </StyledFAQContent>
              <StyledFAQSubTitle>
                What is the Sahara governance token?
              </StyledFAQSubTitle>
              <StyledFAQContent>
                SHRA is the governance token of the Sahara platform. You can participate in the platform governance
                by using SHRA token and vote for functions you’d like to have implemented on the platform.
              </StyledFAQContent>
              <StyledFAQSubTitle>
                Has the protocol been audited?
              </StyledFAQSubTitle>
              <StyledFAQContent>
                Sahara’s vesting contract has been audited. To learn more, go to <a target="_blank" rel="noreferrer" href="https://sahara.network/resources/sahara-security-audit.pdf" >Sahara security audit</a>.
              </StyledFAQContent>
              <StyledFAQSubTitle>
              What is a bridge?
              </StyledFAQSubTitle>
              <StyledFAQContent>
              Bridges exist to connect blockchains to each other, allowing cross-chain transfers of assets and information. 
              Bridges can be used to take advantage of lower transaction fees, obtain native crypto assets, and moving assets between chains, to mention a few. 
              When using these bridges, the tokens are ‘frozen’ on the originating blockchain and an equal amount of tokens are minted 
              on the receiving chain. If you need to transfer your assets cross-chain to use them in the Sahara platform 
              use <a target="_blank" rel="noreferrer" href="https://wallet.polygon.technology/bridge">the Polygon bridge</a>.
              </StyledFAQContent>
              <StyledFAQSubTitle>
                Where can I ask support-related questions?
              </StyledFAQSubTitle>
              <StyledFAQContent>
                You can contact us by writing an email to <a href="mailto:support@sahara.network">support@sahara.network</a>.
              </StyledFAQContent>
            </StyledFAQContainer>
          </Modal>
        </Portal>
      )}
    </>
  );
};

export default FAQ;